import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useSnackbar } from "notistack";
import skuServices from "app/services/sku-services";
import centreServices from "app/services/centre-services";
import warehouseBinServices from "app/services/warehouse-bin-services";

// Posición y existencias de un SKU, dentro de su ficha.
//
// Las dos cosas vienen de sitios distintos y por eso se comportan distinto: la
// posición es de Haulers y se edita acá mismo; las existencias son del ERP, se
// consultan en vivo y son de solo lectura. Mezclarlas en un solo bloque
// editable haría creer que se puede corregir el stock desde acá.
const SkuBinStockPanel = ({ record, onUpdated }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [centres, setCentres] = useState([]);
    const [centreId, setCentreId] = useState("");
    const [binOptions, setBinOptions] = useState([]);
    const [binInput, setBinInput] = useState("");
    const [searchingBins, setSearchingBins] = useState(false);
    const [saving, setSaving] = useState(false);

    const [stock, setStock] = useState(null);
    const [loadingStock, setLoadingStock] = useState(false);
    const [stockLoaded, setStockLoaded] = useState(false);

    // La posición se lleva en estado local porque el drawer no refresca su
    // prop record al guardar: onUpdated solo recarga el listado de atrás. Sin
    // esto, asignar una posición no se vería hasta cerrar y volver a abrir.
    const [currentBin, setCurrentBin] = useState(record?.bin || null);

    useEffect(() => {
        setCurrentBin(record?.bin || null);
    }, [record]);

    useEffect(() => {
        const loadCentres = async () => {
            try {
                const data = await centreServices.searchByCompany();
                const list = Array.isArray(data) ? data : data?.data || [];
                setCentres(list);
                // Se preselecciona la bodega de la posición actual; si no hay,
                // la única de la compañía.
                if (record?.bin?.centre_id) setCentreId(record.bin.centre_id);
                else if (list.length === 1) setCentreId(list[0].id);
            } catch (_error) {
                setCentres([]);
            }
        };
        loadCentres();
    }, [record]);

    const loadStock = useCallback(async () => {
        if (!record?.sku) return;

        try {
            setLoadingStock(true);
            const response = await skuServices.getStock([record.sku]);
            setStock(response?.data?.[record.sku.toLowerCase()] || null);
            setStockLoaded(true);
        } catch (_error) {
            setStock(null);
            setStockLoaded(true);
        } finally {
            setLoadingStock(false);
        }
    }, [record]);

    useEffect(() => {
        setStock(null);
        setStockLoaded(false);
        loadStock();
    }, [loadStock]);

    // Busca posiciones existentes mientras se escribe, pero no obliga a elegir
    // una: si el código no existe todavía, el backend lo crea. El código está
    // escrito en la viga del rack, que es lo que la persona tiene a mano.
    useEffect(() => {
        if (!binInput || binInput.length < 2 || !centreId) {
            setBinOptions([]);
            return undefined;
        }

        const timer = setTimeout(async () => {
            try {
                setSearchingBins(true);
                const data = await warehouseBinServices.getBins(
                    `?centre_id=${centreId}&q=${encodeURIComponent(binInput)}&per_page=20`
                );
                setBinOptions(data?.data || []);
            } catch (_error) {
                setBinOptions([]);
            } finally {
                setSearchingBins(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [binInput, centreId]);

    const needsCentre = useMemo(() => centres.length > 1 && !centreId, [centres, centreId]);

    const saveBin = async (value) => {
        const code = typeof value === "string" ? value : value?.code;
        if (!code?.trim()) return;

        try {
            setSaving(true);
            const updated = await skuServices.setBin(record.id, {
                code: code.trim(),
                centre_id: centreId || undefined,
            });
            enqueueSnackbar(`Posición actualizada a ${updated.bin?.code || code}`, { variant: "success" });
            setCurrentBin(updated.bin || null);
            setBinInput("");
            setBinOptions([]);
            onUpdated(updated);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo guardar la posición", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const clearBin = async () => {
        try {
            setSaving(true);
            const updated = await skuServices.setBin(record.id, {});
            enqueueSnackbar("Posición quitada", { variant: "success" });
            setCurrentBin(null);
            onUpdated(updated);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo quitar la posición", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Posición en bodega
            </Typography>

            {currentBin ? (
                <Paper
                    variant="outlined"
                    sx={{ p: 1.25, mb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                    <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {currentBin.code}
                            </Typography>
                            {currentBin.source === "erp" && <Chip size="small" color="info" label="Del ERP" />}
                            {currentBin.locked && (
                                <Tooltip title="El ERP no la sobrescribe">
                                    <LockOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                                </Tooltip>
                            )}
                        </Stack>
                        {currentBin.pick_sequence !== null && currentBin.pick_sequence !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                                Orden de recorrido #{currentBin.pick_sequence}
                            </Typography>
                        )}
                    </Box>
                    <Tooltip title="Quitar posición">
                        <span>
                            <IconButton size="small" disabled={saving} onClick={clearBin}>
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Paper>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Este producto todavía no tiene posición asignada.
                </Typography>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                {centres.length > 1 && (
                    <TextField
                        select
                        size="small"
                        label="Bodega"
                        value={centreId}
                        onChange={(event) => setCentreId(event.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        {centres.map((centre) => (
                            <MenuItem key={centre.id} value={centre.id}>
                                {centre.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <Autocomplete
                    freeSolo
                    fullWidth
                    options={binOptions}
                    loading={searchingBins}
                    disabled={needsCentre || saving}
                    inputValue={binInput}
                    getOptionLabel={(option) => (typeof option === "string" ? option : option.code || "")}
                    filterOptions={(options) => options}
                    onInputChange={(_event, value) => setBinInput(value)}
                    onChange={(_event, value) => value && saveBin(value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label={currentBin ? "Cambiar posición" : "Asignar posición"}
                            placeholder="A-01-03-B-02"
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && binInput.trim()) {
                                    event.preventDefault();
                                    saveBin(binInput);
                                }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(searchingBins || saving) && <CircularProgress size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Stack>

            {needsCentre && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
                    Elige primero la bodega.
                </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                Si el código no existe todavía se crea. Lo que asignes acá queda bloqueado: la sincronización del ERP
                no lo sobrescribe.
            </Typography>

            <Divider sx={{ my: 2.5 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                    Existencias
                </Typography>
                <Tooltip title="Volver a consultar al ERP">
                    <span>
                        <IconButton size="small" onClick={loadStock} disabled={loadingStock}>
                            <RefreshOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {loadingStock && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                        Consultando al ERP…
                    </Typography>
                </Stack>
            )}

            {!loadingStock && stockLoaded && !stock && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="body2">
                        Sin existencias que mostrar. Las cantidades las entrega el ERP (Relbase o Bsale); si la
                        compañía no tiene uno conectado, o el código no existe allá, esto queda vacío.
                    </Typography>
                </Alert>
            )}

            {!loadingStock && stock && (
                <Box>
                    <Typography variant="h4" sx={{ mb: 0.5 }}>
                        {stock.total ?? "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Total según {stock.source || "el ERP"}, en vivo. Haulers no guarda saldos.
                    </Typography>

                    {stock.by_warehouse?.length > 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Bodega</TableCell>
                                    <TableCell align="right">Stock</TableCell>
                                    <TableCell>Ubicación en el ERP</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stock.by_warehouse.map((row, index) => (
                                    <TableRow key={`${row.warehouse}-${index}`}>
                                        <TableCell>{row.warehouse}</TableCell>
                                        <TableCell align="right">{row.stock ?? "—"}</TableCell>
                                        <TableCell>{row.location || "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {currentBin &&
                        stock.by_warehouse?.some(
                            (row) => row.location && row.location.trim() && row.location !== currentBin.code
                        ) && (
                            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                                <Typography variant="body2">
                                    La ubicación que reporta el ERP no coincide con la que tiene Haulers
                                    ({currentBin.code}). Manda la de Haulers, porque alguien la fijó a mano acá.
                                </Typography>
                            </Alert>
                        )}
                </Box>
            )}
        </Box>
    );
};

export default SkuBinStockPanel;
